<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';

const { t } = useI18n();

const agentRanking = useMapGetter('csat/getAgentRanking');

const MEDALS = ['🥇', '🥈', '🥉'];

const topThree = computed(() => agentRanking.value.slice(0, 3));
const remaining = computed(() => agentRanking.value.slice(3));

// Render order: 2nd, 1st, 3rd — winner in the center, like a podium
const podium = computed(() => {
  const [first, second, third] = topThree.value;
  return [
    second && { agent: second, medal: MEDALS[1], position: 2 },
    first && { agent: first, medal: MEDALS[0], position: 1 },
    third && { agent: third, medal: MEDALS[2], position: 3 },
  ].filter(Boolean);
});

const formatRating = rating => Number(rating).toFixed(2);
</script>

<template>
  <div
    v-if="agentRanking.length"
    class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-6"
  >
    <div class="flex flex-col gap-1 text-center">
      <h3 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('CSAT_REPORTS.AGENT_RANKING.TITLE') }}
      </h3>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ t('CSAT_REPORTS.AGENT_RANKING.DESCRIPTION') }}
      </p>
    </div>

    <div class="flex items-end justify-center gap-4">
      <div
        v-for="entry in podium"
        :key="entry.agent.id"
        class="flex w-44 flex-col items-center gap-2 rounded-xl border bg-n-solid-1 p-4 text-center"
        :class="
          entry.position === 1
            ? 'border-n-brand shadow-md -translate-y-2'
            : 'border-n-weak'
        "
      >
        <span class="text-2xl leading-none">{{ entry.medal }}</span>
        <Thumbnail
          :src="entry.agent.thumbnail"
          :username="entry.agent.name"
          size="40px"
        />
        <span class="text-sm font-medium text-n-slate-12">
          {{ entry.agent.name }}
        </span>
        <span
          v-if="entry.position === 1"
          class="text-xs font-medium text-n-amber-11"
        >
          {{ t('CSAT_REPORTS.AGENT_RANKING.TOP_AGENT') }}
        </span>
        <span class="text-sm font-semibold text-n-blue-text">
          {{
            t('CSAT_REPORTS.AGENT_RANKING.AVERAGE_RATING', {
              rating: formatRating(entry.agent.average_rating),
            })
          }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{
            t(
              'CSAT_REPORTS.AGENT_RANKING.RESPONSE_COUNT',
              { count: entry.agent.response_count },
              entry.agent.response_count
            )
          }}
        </span>
      </div>
    </div>

    <div v-if="remaining.length" class="flex flex-col divide-y divide-n-weak">
      <div
        v-for="(agent, index) in remaining"
        :key="agent.id"
        class="flex items-center gap-3 py-2"
      >
        <span class="w-6 text-sm font-medium text-n-slate-11">
          {{
            t('CSAT_REPORTS.AGENT_RANKING.POSITION', { position: index + 4 })
          }}
        </span>
        <Thumbnail :src="agent.thumbnail" :username="agent.name" size="24px" />
        <span class="flex-1 truncate text-sm text-n-slate-12">
          {{ agent.name }}
        </span>
        <span class="text-sm font-medium text-n-blue-text">
          {{
            t('CSAT_REPORTS.AGENT_RANKING.AVERAGE_RATING', {
              rating: formatRating(agent.average_rating),
            })
          }}
        </span>
        <span class="w-24 text-right text-xs text-n-slate-11">
          {{
            t(
              'CSAT_REPORTS.AGENT_RANKING.RESPONSE_COUNT',
              { count: agent.response_count },
              agent.response_count
            )
          }}
        </span>
      </div>
    </div>
  </div>
</template>
